import {
  ITableHeaders,
  TableToolTipProps,
} from "@/components/Table/table.interface";
import ColumnHeaderTooltip from "@/components/Tooltip/ColumnHeaderTooltip";
import { Order } from "@/types";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { SxProps } from "@mui/system";
import { visuallyHidden } from "@mui/utils";
import * as React from "react";
import { StyledTableCell } from ".";

export default function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    headCells,
    isCheck,
    toolTipProps,
    isToolTip,
    tableHeaderStyles,
    isEditing = false,
  } = props;
  const createSortHandler =
    (property: any) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell: any, index: number) => {
          // Reusing the `noSort` path rather than disabling the control:
          // there are only two paths here, and a `<TableSortLabel disabled>`
          // still renders an arrow that looks like it should do something.
          // While editing, sorting would reorder rows by their SAVED values
          // under a user who is typing — see the note where this is passed.
          const label = headCell.noSort || isEditing ? (
            headCell.label
          ) : (
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          );

          const heading =
            isToolTip && headCell?.toolTip ? (
              <ColumnHeaderTooltip
                title={headCell.toolTip}
                toolTipProps={toolTipProps}
              >
                {label}
              </ColumnHeaderTooltip>
            ) : (
              label
            );

          return (
            <StyledTableCell
              key={headCell.id}
              sortDirection={orderBy === headCell.id ? order : false}
              align={headCell.align ?? "left"}
              colSpan={headCell.colSpan ?? 1}
              // No inline width. `CustomTable`'s generated rules already size
              // every column, and they sit behind the `data-columns-match` gate;
              // an inline width here survived that gate, so when the reconciler
              // detected a header/cell mismatch and dropped to auto layout, these
              // widths stayed and the fallback never actually released sizing.
              sx={{ ...tableHeaderStyles }}
            >
              {headCell.id && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    /**
                     * The column's own alignment, applied to the flex axis.
                     *
                     * `align` on the cell only sets `text-align`, which a FLEX
                     * CHILD ignores — so this wrapper stayed at `flex-start` and
                     * every right-aligned header sat left of its own values,
                     * while the body cells (plain text) hugged the right padding
                     * edge. The gap was the difference between the label's width
                     * and the column's, so it varied per column: measured 8px on
                     * Impressions, 17 on Clicks, 30 on CTR, 33 on Δ Impr.
                     *
                     * `flex-start` is what the wrapper already resolved to for
                     * left and unaligned columns, so nothing without an explicit
                     * `align: "right" | "center"` moves.
                     */
                    justifyContent:
                      headCell.align === "right"
                        ? "flex-end"
                        : headCell.align === "center"
                        ? "center"
                        : "flex-start",
                    gap: headCell?.noSort ? 0.5 : 0,
                  }}
                >
                  <>
                    {isCheck && index === 0 && (
                      <Checkbox
                        color="primary"
                        indeterminate={
                          numSelected > 0 && numSelected < rowCount
                        }
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                      />
                    )}
                  </>
                  {heading}
                </Box>
              )}
            </StyledTableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: any) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  headCells: Array<ITableHeaders>;
  isCheck: boolean;
  isToolTip: boolean;
  toolTipProps: TableToolTipProps;
  tableHeaderStyles: SxProps;
  /** Freezes the sort controls — see where this is passed in `CustomTable`. */
  isEditing?: boolean;
}
